import { useState } from 'react';
import {
  applyMode,
  getInitialMode,
  storeMode,
  type ThemeMode,
} from '../lib/theme';

function SunIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
      />
    </svg>
  );
}

/**
 * Segmented light/dark toggle. The active side is filled with the Cloudscape
 * blue so it's clearly visible on any background and shows the current mode.
 * Flipping the mode sets `data-theme` on <html>, restyling the whole app.
 */
export default function ThemeToggle({ className }: { className?: string }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  const set = (next: ThemeMode) => {
    if (next === mode) return;
    setMode(next);
    applyMode(next);
    storeMode(next);
  };

  const segBase =
    'flex h-7 w-8 items-center justify-center rounded-full transition-colors';

  return (
    <div
      role="group"
      aria-label="Theme"
      className={`inline-flex items-center gap-0.5 rounded-full border border-primary/40 p-0.5 ${
        className ?? ''
      }`}
    >
      <button
        type="button"
        onClick={() => set('light')}
        aria-label="Light mode"
        aria-pressed={mode === 'light'}
        title="Light mode"
        className={`${segBase} ${
          mode === 'light'
            ? 'bg-primary text-primary-content'
            : 'opacity-70 hover:bg-primary/15 hover:opacity-100'
        }`}
      >
        <SunIcon />
      </button>
      <button
        type="button"
        onClick={() => set('dark')}
        aria-label="Dark mode"
        aria-pressed={mode === 'dark'}
        title="Dark mode"
        className={`${segBase} ${
          mode === 'dark'
            ? 'bg-primary text-primary-content'
            : 'opacity-70 hover:bg-primary/15 hover:opacity-100'
        }`}
      >
        <MoonIcon />
      </button>
    </div>
  );
}
